(function () {
  const payloadSelector =
    'script[type="application/json"][data-view-item-list-payload]';

  const trackedAttribute =
    'data-view-item-list-tracked';

  const trackedFingerprints = new Set();

  /**
   * Send GA4 event.
   */
  function getConfiguredGa4Id() {
    window.dataLayer = window.dataLayer || [];

    for (const entry of window.dataLayer) {
      if (
        entry &&
        entry[0] === 'config' &&
        typeof entry[1] === 'string' &&
        entry[1].startsWith('G-')
      ) {
        return entry[1];
      }
    }

    return null;
  }

  function sendGa4Event(eventName, params) {
    window.dataLayer = window.dataLayer || [];

    if (typeof window.gtag !== 'function') {
      window.gtag = function () {
        window.dataLayer.push(arguments);
      };
    }

    let attempts = 0;
    const maxAttempts = 30;

    function sendWhenReady() {
      const ga4Id = getConfiguredGa4Id();

      if (ga4Id) {
        window.gtag('event', eventName, {
          ...params,
          send_to: ga4Id
        });

        return;
      }

      attempts += 1;

      if (attempts >= maxAttempts) {
        console.warn(
          'GA4 configuration was not ready for event:',
          eventName
        );

        return;
      }

      window.setTimeout(sendWhenReady, 100);
    }

    sendWhenReady();
  }

  /**
   * Create a unique fingerprint for each list
   * to prevent duplicate tracking on the same page.
   */
  function getPayloadFingerprint(payload) {
    const ecommerce = payload?.ecommerce;
    const items = Array.isArray(ecommerce?.items) ? ecommerce.items : [];

    const itemIds = items
      .map((item) => item.item_id || item.item_name || '')
      .join('|');

    return `${window.location.pathname}${window.location.search}|${ecommerce?.item_list_id || ''}|${itemIds}`;
  }

  /**
   * Send view_item_list once.
   */
  function sendViewItemList(script) {
    if (!script) return;

    if (script.hasAttribute(trackedAttribute)) {
      return;
    }

    try {
      const payload = JSON.parse(
        script.textContent
      );

      const items =
        payload?.ecommerce?.items;

      if (
        !payload?.event ||
        !Array.isArray(items) ||
        !items.length
      ) {
        return;
      }

      const fingerprint =
        getPayloadFingerprint(payload);

      script.setAttribute(
        trackedAttribute,
        'true'
      );

      if (
        trackedFingerprints.has(
          fingerprint
        )
      ) {
        return;
      }

      trackedFingerprints.add(
        fingerprint
      );

      sendGa4Event(
        payload.event,
        payload.ecommerce
      );
    } catch (error) {
      console.warn(
        'view_item_list tracking payload is invalid',
        error
      );
    }
  }

  /**
   * Observe product list visibility.
   *
   * threshold: 0 means the event fires as soon as
   * any part of the product list enters the viewport.
   */
  function observePayload(script) {
    if (!script) return;

    let payload;

    try {
      payload = JSON.parse(script.textContent);
    } catch (error) {
      console.warn('view_item_list tracking payload is invalid', error);
      return;
    }

    /*
    * Search Results:
    * Products are already rendered as part of the search result page,
    * so fire view_item_list immediately.
    */
    if (payload?.ecommerce?.item_list_id === 'search-results') {
      sendViewItemList(script);
      return;
    }

    /*
    * Other product lists:
    * Complete Your Routine, Shine Strong, Featured Collection, etc.
    * Track only when the section becomes visible.
    */
    const section =
      script.closest('[data-view-item-list-section]') ||
      script.parentElement;

    if (!('IntersectionObserver' in window) || !section) {
      sendViewItemList(script);
      return;
    }

    const observer = new IntersectionObserver(
      (entries) => {
        const entry = entries[0];

        if (!entry.isIntersecting) return;

        observer.disconnect();

        sendViewItemList(script);
      },
      {
        threshold: 0.25
      }
    );

    observer.observe(section);
  }

  /**
   * Find all tracking payloads inside a DOM root.
   */
  function observePayloads(root) {
    if (!root?.querySelectorAll) {
      return;
    }

    root
      .querySelectorAll(
        payloadSelector
      )
      .forEach(function (script) {
        if (
          script.hasAttribute(
            trackedAttribute
          )
        ) {
          return;
        }

        observePayload(script);
      });
  }

  /**
   * Handle dynamically loaded Shopify sections.
   *
   * Important for:
   * - Product recommendations
   * - AJAX-filtered Search Results
   * - Dynamically refreshed sections
   */
  function observeDynamicContent() {
    if (
      !('MutationObserver' in window)
    ) {
      return;
    }

    const observer =
      new MutationObserver(
        function (mutations) {
          mutations.forEach(
            function (mutation) {
              mutation.addedNodes.forEach(
                function (node) {
                  if (
                    node.nodeType !==
                    Node.ELEMENT_NODE
                  ) {
                    return;
                  }

                  if (
                    node.matches?.(
                      payloadSelector
                    )
                  ) {
                    observePayload(node);
                    return;
                  }

                  observePayloads(node);
                }
              );
            }
          );
        }
      );

    observer.observe(
      document.body,
      {
        childList: true,
        subtree: true
      }
    );
  }

  /**
   * Reset when browser restores a page using BFCache.
   *
   * Example:
   * Search Results
   * → PDP
   * → Browser Back
   * → view_item_list should fire again.
   */
  function resetViewItemListTracking() {
    trackedFingerprints.clear();

    document
      .querySelectorAll(
        `${payloadSelector}[${trackedAttribute}]`
      )
      .forEach(function (script) {
        script.removeAttribute(
          trackedAttribute
        );
      });

    observePayloads(document);
  }

  /**
   * Initialize tracking.
   */
  function initViewItemListTracking() {
    observePayloads(document);

    observeDynamicContent();
  }

  if (
    document.readyState === 'loading'
  ) {
    document.addEventListener(
      'DOMContentLoaded',
      initViewItemListTracking,
      {
        once: true
      }
    );
  } else {
    initViewItemListTracking();
  }

  /**
   * Browser Back / Forward.
   */
  window.addEventListener(
    'pageshow',
    function (event) {
      if (!event.persisted) return;

      resetViewItemListTracking();
    }
  );
})();